/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Edit2, Trash2, Video, Image as ImageIcon, Music, Radio,
  FileQuestion, Play, Copy, Check, Calendar, Clock, User, Building2,
  Layers, Tag, FileText, Sparkles, RefreshCw, Hash, Box, FileEdit,
  Activity, Inbox, Share2, Link2, Ban, UserCheck, ListTree, StickyNote
} from 'lucide-react';
import { serviceRequestService } from '../services/serviceRequestService';
import Modal from '../components/Modal';
import MediaViewerModal from '../components/MediaViewerModal';
import StatusBadge from '../components/StatusBadge';
import PublicLinkExpirySelector from '../components/PublicLinkExpirySelector';
import QRCodeDisplay from '../components/QRCode';
import toastService from '../utils/toastService';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import { usePermissions } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';
import useServiceRequestTypes from '../hooks/useServiceRequestTypes';
import { resolveTypeName, DEFAULT_REQUEST_NUMBER_LABEL } from '../utils/serviceRequestLabels';
import {
  SERVICE_REQUEST_MEDIA_FILE_TYPES,
  SERVICE_REQUEST_MEDIA_FILE_TYPE_LABELS,
  SERVICE_REQUEST_EVENT_LABELS,
  SERVICE_REQUEST_NOTE_MAX_LENGTH,
} from '../constants/serviceRequests';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL, SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const SERVICE_REQUESTS_ROUTE = '/service-requests';

const MEDIA_TYPE_ICONS = {
  video: Video,
  image: ImageIcon,
  audio: Music,
  session_recording: Radio,
};

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatDateTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) { return '—'; }
  if (bytes < 1024) { return `${bytes} B`; }
  if (bytes < 1024 * 1024) { return `${(bytes / 1024).toFixed(1)} KB`; }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const parseJsonSafe = (str) => {
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};

const FIELD_CHANGE_EVENT_TYPES = [
  'type_changed',
  'hierarchy_changed',
  'customer_name_changed',
  'asset_identifier_changed',
  'equipment_identifier_changed',
  'assignee_changed',
];

const EVENT_ICON_CONFIG = {
  created: { icon: Sparkles, color: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20' },
  status_changed: { icon: RefreshCw, color: 'text-primary bg-primary/10 border-primary/20' },
  type_changed: { icon: Tag, color: 'text-sky-500 bg-sky-500/10 border-sky-500/20' },
  hierarchy_changed: { icon: ListTree, color: 'text-cyan-500 bg-cyan-500/10 border-cyan-500/20' },
  customer_name_changed: { icon: User, color: 'text-amber-500 bg-amber-500/10 border-amber-500/20' },
  asset_identifier_changed: { icon: Hash, color: 'text-indigo-500 bg-indigo-500/10 border-indigo-500/20' },
  equipment_identifier_changed: { icon: Box, color: 'text-purple-500 bg-purple-500/10 border-purple-500/20' },
  additional_details_changed: { icon: FileEdit, color: 'text-teal-500 bg-teal-500/10 border-teal-500/20' },
  media_added: { icon: Video, color: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20' },
  media_removed: { icon: Trash2, color: 'text-error bg-error/10 border-error/20' },
  note_added: { icon: StickyNote, color: 'text-amber-500 bg-amber-500/10 border-amber-500/20' },
  assignee_changed: { icon: UserCheck, color: 'text-rose-500 bg-rose-500/10 border-rose-500/20' },
  public_link_generated: { icon: Link2, color: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20' },
  public_link_regenerated: { icon: RefreshCw, color: 'text-primary bg-primary/10 border-primary/20' },
  public_link_expiry_updated: { icon: Clock, color: 'text-amber-500 bg-amber-500/10 border-amber-500/20' },
  public_link_revoked: { icon: Ban, color: 'text-error bg-error/10 border-error/20' },
  public_link_accessed: { icon: Share2, color: 'text-sky-500 bg-sky-500/10 border-sky-500/20' },
  live_session_joined: { icon: Radio, color: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20' },
};

const CopyButton = ({ text, label = 'value', className = '' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toastService.success(`Copied ${label} to clipboard`);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!text) return null;

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={`Copy ${label}`}
      className={`p-1.5 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-lg transition-all ${className}`}
    >
      {copied ? <Check size={13} className="text-success" /> : <Copy size={13} />}
    </button>
  );
};

const renderEventDescription = (event, types) => {
  const label = SERVICE_REQUEST_EVENT_LABELS[event.eventType] || event.eventType;
  if (event.eventType === 'status_changed') {
    return (
      <span className="text-sm font-bold text-base-content">
        Status changed from <span className="px-1.5 py-0.5 rounded bg-base-content/10 text-xs font-mono font-bold text-base-content/70">{event.oldStatus}</span> to <span className="px-1.5 py-0.5 rounded bg-primary/15 text-xs font-mono font-bold text-primary">{event.newStatus}</span>
      </span>
    );
  }
  if (event.eventType === 'type_changed') {
    const oldLabel = event.oldValue ? resolveTypeName(event.oldValue, types) : '—';
    const newLabel = event.newValue ? resolveTypeName(event.newValue, types) : '—';
    return (
      <span className="text-sm font-bold text-base-content">
        {label} from <span className="font-semibold text-base-content/60">"{oldLabel}"</span> to <span className="font-bold text-primary">"{newLabel}"</span>
      </span>
    );
  }
  if (event.eventType === 'additional_details_changed') {
    return (
      <span className="text-sm font-bold text-base-content">
        {event.newValue ? (
          <>Additional details updated to <span className="font-normal text-base-content/80">"{event.newValue}"</span></>
        ) : (
          <span className="text-base-content/50">Additional details removed</span>
        )}
      </span>
    );
  }
  if (FIELD_CHANGE_EVENT_TYPES.includes(event.eventType)) {
    return (
      <span className="text-sm font-bold text-base-content">
        {label} from <span className="font-semibold text-base-content/60">"{event.oldValue ?? '—'}"</span> to <span className="font-bold text-primary">"{event.newValue ?? '—'}"</span>
      </span>
    );
  }
  if (event.eventType === 'public_link_generated' || event.eventType === 'public_link_regenerated') {
    const info = parseJsonSafe(event.newValue);
    if (info?.code) {
      return (
        <span className="text-sm font-bold text-base-content">
          {label}: <span className="font-mono text-xs font-medium text-primary">{info.code}</span>
          {info.expiresAt && (
            <> · expires <span className="font-semibold text-base-content/70">{formatDateTime(info.expiresAt)}</span></>
          )}
        </span>
      );
    }
  }
  if (event.eventType === 'public_link_expiry_updated') {
    const oldInfo = parseJsonSafe(event.oldValue);
    const newInfo = parseJsonSafe(event.newValue);
    return (
      <span className="text-sm font-bold text-base-content">
        {label} from <span className="font-semibold text-base-content/60">"{oldInfo?.expiresAt ? formatDateTime(oldInfo.expiresAt) : '—'}"</span> to <span className="font-bold text-primary">"{newInfo?.expiresAt ? formatDateTime(newInfo.expiresAt) : '—'}"</span>
      </span>
    );
  }
  if (event.eventType === 'note_added' && event.detail) {
    return (
      <span className="text-sm font-bold text-base-content">
        {label}: <span className="font-normal text-base-content/80 whitespace-pre-wrap break-words">"{event.detail}"</span>
      </span>
    );
  }
  if (event.eventType === 'live_session_joined' && event.detail) {
    return <span className="text-sm font-bold text-base-content">{event.detail}</span>;
  }
  if ((event.eventType === 'media_added' || event.eventType === 'media_removed') && event.detail) {
    return (
      <span className="text-sm font-bold text-base-content">
        {label}: <span className="font-mono text-xs font-medium text-base-content/80">{event.detail}</span>
      </span>
    );
  }
  return <span className="text-sm font-bold text-base-content">{label}</span>;
};

const MediaCard = ({ item, onRemove, canRemove, onOpen }) => {
  const Icon = MEDIA_TYPE_ICONS[item.fileType] || FileQuestion;
  const [previewFailed, setPreviewFailed] = useState(false);
  const isImage = item.fileType === 'image';
  const isVideoLike = item.fileType === 'video' || item.fileType === 'session_recording';
  const showRealPreview = (isImage || isVideoLike) && !previewFailed;

  return (
    <div className="relative group bg-base-100 border border-base-content/10 hover:border-primary/30 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 flex flex-col">
      <button
        type="button"
        onClick={() => onOpen(item)}
        className="block w-full text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40 rounded-t-xl overflow-hidden"
      >
        <div className="aspect-[16/10] bg-base-200 relative overflow-hidden flex items-center justify-center group-hover:brightness-105 transition-all">
          {showRealPreview ? (
            isImage ? (
              <img
                src={item.fileUrl}
                alt={item.fileName}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                onError={() => setPreviewFailed(true)}
              />
            ) : (
              <>
                <video
                  src={item.fileUrl}
                  muted
                  preload="metadata"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={() => setPreviewFailed(true)}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/15 transition-colors">
                  <div className="w-9 h-9 rounded-full bg-primary text-primary-content flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                    <Play size={14} className="fill-current ml-0.5" />
                  </div>
                </div>
              </>
            )
          ) : (
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
              <Icon size={20} />
            </div>
          )}

          <div className="absolute top-2 left-2 pointer-events-none">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider bg-black/60 text-white backdrop-blur-md">
              <Icon size={10} />
              {SERVICE_REQUEST_MEDIA_FILE_TYPE_LABELS[item.fileType] || item.fileType}
            </span>
          </div>
        </div>
      </button>

      {canRemove && (
        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 transition-opacity duration-200 z-10">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(item); }}
            className="p-1.5 rounded-lg bg-base-100/90 hover:bg-error hover:text-error-content text-base-content/60 shadow-md backdrop-blur-sm transition-all"
            title="Remove media"
            aria-label="Remove media"
          >
            <Trash2 size={13} />
          </button>
        </div>
      )}

      <div className="p-3 flex-1 flex flex-col justify-between gap-2">
        <div className="min-w-0">
          <p
            className="text-xs font-bold text-base-content truncate"
            title={item.fileName}
          >
            {item.fileName}
          </p>
        </div>
        <div className="flex items-center justify-between text-[10px] text-base-content/50 font-medium pt-1 border-t border-base-content/5">
          <span>{formatFileSize(item.fileSize)}</span>
          <span title={formatDateTime(item.createdAt)}>{formatRelativeTime(item.createdAt)}</span>
        </div>
      </div>
    </div>
  );
};

const formatFutureExpiry = (date) => {
  if (!date) return '—';
  const target = new Date(date);
  if (isNaN(target.getTime())) return '—';
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return 'Expired';
  const diffMinutes = Math.round(diffMs / 60000);
  if (diffMinutes < 60) return `in ${diffMinutes} min${diffMinutes === 1 ? '' : 's'}`;
  const diffHours = Math.round(diffMs / 3600000);
  if (diffHours < 24) return `in ${diffHours} hour${diffHours === 1 ? '' : 's'}`;
  const diffDays = Math.round(diffMs / 86400000);
  return `in ${diffDays} day${diffDays === 1 ? '' : 's'}`;
};

const ServiceRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = usePermissions();
  const { user } = useAuth();
  const showAccountTile = !user?.accountUID;
  const showSubAccountTile = !user?.subAccountUID;

  const [serviceRequest, setServiceRequest] = useState(null);
  const { types } = useServiceRequestTypes(serviceRequest?.subAccountUid, { includeInactive: true });
  const [loading, setLoading] = useState(true);

  const [mediaItems, setMediaItems] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all');
  const [viewerItem, setViewerItem] = useState(null);

  const [events, setEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(true);

  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [activityTab, setActivityTab] = useState('notes');

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [removeMediaTarget, setRemoveMediaTarget] = useState(null);
  const [removingMedia, setRemovingMedia] = useState(false);

  const [publicLink, setPublicLink] = useState(null);
  const [publicLinkLoading, setPublicLinkLoading] = useState(true);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkExpiryIso, setLinkExpiryIso] = useState('');
  const [isExpiryValid, setIsExpiryValid] = useState(true);
  const [linkActionLoading, setLinkActionLoading] = useState(false);
  const [revokeConfirmOpen, setRevokeConfirmOpen] = useState(false);
  const [showExpiryEditor, setShowExpiryEditor] = useState(false);

  const loadServiceRequest = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await serviceRequestService.getServiceRequestById(id);
      setServiceRequest(data);
      setNotes(data.notes || []);
    } catch {
      toastService.error(`Failed to load ${SERVICE_REQUEST_LABEL.singularLower}`);
      navigate(SERVICE_REQUESTS_ROUTE);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadMediaItems = useCallback(async () => {
    try {
      setMediaLoading(true);
      const { data } = await serviceRequestService.getMediaItems(id);
      setMediaItems(data || []);
    } catch {
      toastService.error('Failed to load media items');
    } finally {
      setMediaLoading(false);
    }
  }, [id]);

  const loadEvents = useCallback(async () => {
    try {
      setTimelineLoading(true);
      const { data } = await serviceRequestService.getEvents(id);
      setEvents(data || []);
    } catch {
      toastService.error('Failed to load activity timeline');
    } finally {
      setTimelineLoading(false);
    }
  }, [id]);

  const loadPublicLink = useCallback(async () => {
    try {
      setPublicLinkLoading(true);
      const { data } = await serviceRequestService.getPublicLink(id);
      setPublicLink(data);
    } catch {
      toastService.error('Failed to load public link status');
    } finally {
      setPublicLinkLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadServiceRequest();
    loadMediaItems();
    loadEvents();
    loadPublicLink();
  }, [loadServiceRequest, loadMediaItems, loadEvents, loadPublicLink]);

  useEffect(() => {
    if (location.state?.openLinkModal) {
      setShowLinkModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await serviceRequestService.deleteServiceRequest(id);
      toastService.success(`${SERVICE_REQUEST_LABEL.singular} deleted successfully`);
      navigate(SERVICE_REQUESTS_ROUTE);
    } catch (err) {
      toastService.error(err.response?.data?.message || `Failed to delete ${SERVICE_REQUEST_LABEL.singularLower}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleRemoveMedia = async () => {
    try {
      setRemovingMedia(true);
      await serviceRequestService.removeMediaItem(id, removeMediaTarget.uid);
      toastService.success('Media item removed successfully');
      setRemoveMediaTarget(null);
      loadMediaItems();
      loadEvents();
    } catch (err) {
      toastService.error(err.response?.data?.message || 'Failed to remove media item');
    } finally {
      setRemovingMedia(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    const note = noteDraft.trim();
    if (!note) return;
    try {
      setAddingNote(true);
      const { data } = await serviceRequestService.addNote(id, note);
      setNotes((prev) => [data, ...prev]);
      setNoteDraft('');
      setActivityTab('notes');
      toastService.success('Note added successfully');
      loadEvents();
    } catch (err) {
      toastService.error(err.response?.data?.message || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const isLinkActive = publicLink?.status === 'active';
  // Mirrors the list grid: without create permission there's nothing to do unless a link is already active
  const isPublicLinkDisabled = !publicLinkLoading && !isLinkActive && !can.service_requests.create();
  const publicLinkUrl = publicLink ? `${window.location.origin}/s/${publicLink.code}` : '';

  const handleGenerateOrRegenerateLink = async () => {
    if (!linkExpiryIso || !isExpiryValid) {
      toastService.error('Please select a valid future expiry date and time');
      return false;
    }
    try {
      setLinkActionLoading(true);
      const expiresAt = linkExpiryIso;
      const apiCall = isLinkActive
        ? serviceRequestService.updatePublicLinkExpiry
        : serviceRequestService.createPublicLink;
      const { data } = await apiCall(id, { expiresAt });
      setPublicLink(data);
      toastService.success(isLinkActive ? 'Public link expiry updated' : 'Public link generated');
      loadEvents();
      return true;
    } catch (err) {
      toastService.error(err.response?.data?.message || 'Failed to update public link');
      return false;
    } finally {
      setLinkActionLoading(false);
    }
  };

  const handleRevokeLink = async () => {
    try {
      setLinkActionLoading(true);
      await serviceRequestService.revokePublicLink(id);
      toastService.success('Public link revoked');
      setRevokeConfirmOpen(false);
      loadPublicLink();
      loadEvents();
    } catch (err) {
      toastService.error(err.response?.data?.message || 'Failed to revoke public link');
    } finally {
      setLinkActionLoading(false);
    }
  };

  const filteredMediaItems = mediaTypeFilter === 'all'
    ? mediaItems
    : mediaItems.filter(m => m.fileType === mediaTypeFilter);

  const getMediaCountByType = (type) => {
    if (type === 'all') return mediaItems.length;
    return mediaItems.filter(m => m.fileType === type).length;
  };

  if (loading || !serviceRequest) {
    return (
      <div className="space-y-6 animate-pulse p-2">
        <div className="h-6 w-48 bg-base-300 rounded-lg"></div>
        <div className="glass-card rounded-2xl p-6 h-36 bg-base-100/60"></div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-8 space-y-6">
            <div className="glass-card rounded-2xl p-6 h-64 bg-base-100/60"></div>
            <div className="glass-card rounded-2xl p-6 h-64 bg-base-100/60"></div>
          </div>
          <div className="lg:col-span-4">
            <div className="glass-card rounded-2xl p-6 h-96 bg-base-100/60"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Top Breadcrumb */}
      <div>
        <button
          onClick={() => navigate(SERVICE_REQUESTS_ROUTE)}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to {SERVICE_REQUEST_LABEL.plural}
        </button>
      </div>

      {/* Hero Header Banner */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-2 min-w-0">
            {/* Badges & Meta */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
                {serviceRequest.requestNumberLabel || DEFAULT_REQUEST_NUMBER_LABEL}
              </span>
              <StatusBadge status={serviceRequest.status} />
              {(serviceRequest.typeLabel || serviceRequest.type) && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-primary/10 text-primary border border-primary/20">
                  <Tag size={11} />
                  {serviceRequest.typeLabel || serviceRequest.type}
                </span>
              )}
            </div>

            {/* Title & Copy */}
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
                {serviceRequest.requestNumber}
              </h1>
              <CopyButton text={serviceRequest.requestNumber} label={serviceRequest.requestNumberLabel || DEFAULT_REQUEST_NUMBER_LABEL} />
            </div>

            {/* Timestamp Meta */}
            <div className="flex items-center gap-4 text-xs text-base-content/50 font-medium flex-wrap pt-0.5">
              <span className="flex items-center gap-1.5" title={formatDateTime(serviceRequest.createdAt)}>
                <Calendar size={13} className="text-base-content/40" />
                Created {formatDate(serviceRequest.createdAt)}
              </span>
              <span className="flex items-center gap-1.5" title={formatRelativeTime(serviceRequest.createdAt)}>
                <Clock size={13} className="text-base-content/40" />
                {formatTime(serviceRequest.createdAt)}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 sm:self-start md:self-center flex-shrink-0 pt-2 md:pt-0 border-t md:border-t-0 border-base-content/5">
            {can.service_requests.view() && (
              <div
                className={`tooltip tooltip-bottom tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap ${isPublicLinkDisabled ? 'cursor-not-allowed' : ''}`}
                data-tip={isPublicLinkDisabled ? 'No public link generated' : 'Public Link'}
              >
                <button
                  onClick={() => setShowLinkModal(true)}
                  disabled={isPublicLinkDisabled}
                  className="btn btn-sm btn-ghost hover:bg-primary/10 hover:text-primary text-base-content/60 rounded-xl gap-2 font-bold transition-all disabled:bg-transparent disabled:opacity-40 disabled:pointer-events-none"
                  aria-label={isPublicLinkDisabled ? 'No public link generated' : 'Public Link'}
                >
                  <Share2 size={14} />
                  <span>Public Link</span>
                </button>
              </div>
            )}
            {can.service_requests.edit() && (
              <button
                onClick={() => navigate(`/service-requests/${id}/edit`)}
                className="btn btn-sm btn-primary rounded-xl gap-2 font-bold shadow-sm hover:shadow transition-all"
              >
                <Edit2 size={14} />
                <span>Edit</span>
              </button>
            )}
            {can.service_requests.delete() && (
              <button
                onClick={() => setDeleteConfirmOpen(true)}
                className="btn btn-sm btn-ghost hover:bg-error/10 hover:text-error text-base-content/60 rounded-xl gap-2 font-bold transition-all"
                title={`Delete ${SERVICE_REQUEST_LABEL.singular}`}
              >
                <Trash2 size={14} />
                <span>Delete</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Details + Media */}
        <div className="lg:col-span-7 xl:col-span-8 space-y-6">
          {/* Key Information Card */}
          <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-base-content/10">
              <FileText size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                {SERVICE_REQUEST_LABEL.singular} Details
              </h2>
            </div>

            {/* Structured Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              {/* Account (if visible) */}
              {showAccountTile && (
                <div className="bg-base-200/60 p-3.5 rounded-xl border border-base-content/5 flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-sky-500/10 text-sky-500 flex-shrink-0 mt-0.5">
                    <Building2 size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-black uppercase tracking-widest text-base-content/40 mb-0.5">{ACCOUNT_LABEL.singular}</p>
                    <p className="text-sm font-bold text-base-content truncate">{serviceRequest.accountName || '—'}</p>
                  </div>
                </div>
              )}

              {/* Sub-Account (if visible) */}
              {showSubAccountTile && (
                <div className="bg-base-200/60 p-3.5 rounded-xl border border-base-content/5 flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-teal-500/10 text-teal-500 flex-shrink-0 mt-0.5">
                    <Layers size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-black uppercase tracking-widest text-base-content/40 mb-0.5">{SUB_ACCOUNT_LABEL.singular}</p>
                    <p className="text-sm font-bold text-base-content truncate">{serviceRequest.subAccountName || '—'}</p>
                  </div>
                </div>
              )}

              {/* Type option hierarchy — one tile per selected level */}
              {(serviceRequest.hierarchySelections || []).map((sel) => (
                <div key={`${sel.depth}-${sel.nodeUid}`} className="bg-base-200/60 p-3.5 rounded-xl border border-base-content/5 flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-cyan-500/10 text-cyan-500 flex-shrink-0 mt-0.5">
                    <ListTree size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-black uppercase tracking-widest text-base-content/40 mb-0.5">{sel.levelName}</p>
                    <p className="text-sm font-bold text-base-content truncate" title={sel.nodeName}>{sel.nodeName}</p>
                  </div>
                </div>
              ))}

              {/* Asset Identifier */}
              <div className="bg-base-200/60 p-3.5 rounded-xl border border-base-content/5 flex items-start gap-3 justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-500 flex-shrink-0 mt-0.5">
                    <Hash size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-black uppercase tracking-widest text-base-content/40 mb-0.5">
                      {serviceRequest.assetIdentifierLabel || 'Asset Identifier'}
                    </p>
                    <p className="text-sm font-bold text-base-content truncate" title={serviceRequest.assetIdentifier}>
                      {serviceRequest.assetIdentifier || '—'}
                    </p>
                  </div>
                </div>
                {serviceRequest.assetIdentifier && (
                  <CopyButton text={serviceRequest.assetIdentifier} label={serviceRequest.assetIdentifierLabel || 'Asset Identifier'} />
                )}
              </div>

              {/* Equipment Identifier */}
              <div className="bg-base-200/60 p-3.5 rounded-xl border border-base-content/5 flex items-start gap-3 justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-purple-500/10 text-purple-500 flex-shrink-0 mt-0.5">
                    <Box size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-black uppercase tracking-widest text-base-content/40 mb-0.5">
                      {serviceRequest.equipmentIdentifierLabel || 'Equipment Identifier'}
                    </p>
                    <p className="text-sm font-bold text-base-content truncate" title={serviceRequest.equipmentIdentifier}>
                      {serviceRequest.equipmentIdentifier || '—'}
                    </p>
                  </div>
                </div>
                {serviceRequest.equipmentIdentifier && (
                  <CopyButton text={serviceRequest.equipmentIdentifier} label={serviceRequest.equipmentIdentifierLabel || 'Equipment Identifier'} />
                )}
              </div>
            </div>

            {/* Additional Details Sub-Section */}
            <div className="mt-4 pt-4 border-t border-base-content/10">
              <div className="flex items-center gap-2 mb-2">
                <FileEdit size={14} className="text-base-content/40" />
                <p className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
                  Additional Details
                </p>
              </div>
              <div className="bg-base-200/50 p-4 rounded-xl border border-base-content/5">
                {serviceRequest.additionalDetails ? (
                  <p className="text-sm text-base-content leading-relaxed whitespace-pre-wrap break-words">
                    {serviceRequest.additionalDetails}
                  </p>
                ) : (
                  <p className="text-xs text-base-content/40 italic">
                    No additional details provided
                  </p>
                )}
              </div>
            </div>

            {/* Customer & Assignment Sub-Section */}
            <div className="mt-4 pt-4 border-t border-base-content/10">
              <div className="flex items-center gap-2 mb-2">
                <UserCheck size={14} className="text-base-content/40" />
                <p className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
                  {serviceRequest.customerNameLabel || 'Customer Name'} & Assignment
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {/* Customer */}
                <div className="bg-base-200/60 p-3.5 rounded-xl border border-base-content/5 flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/10 text-amber-500 flex-shrink-0 mt-0.5">
                    <User size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-black uppercase tracking-widest text-base-content/40 mb-0.5">{serviceRequest.customerNameLabel || 'Customer Name'}</p>
                    <p className="text-sm font-bold text-base-content truncate">{serviceRequest.customerName || '—'}</p>
                  </div>
                </div>

                {/* Assigned To */}
                <div className="bg-base-200/60 p-3.5 rounded-xl border border-base-content/5 flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-rose-500/10 text-rose-500 flex-shrink-0 mt-0.5">
                    <UserCheck size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-black uppercase tracking-widest text-base-content/40 mb-0.5">Assigned To</p>
                    <p className="text-sm font-bold text-base-content truncate">{serviceRequest.assignedUserName || 'Unassigned'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Media Section */}
          <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
            {/* Media Header & Filter Tabs */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-3 border-b border-base-content/10">
              <div className="flex items-center gap-2">
                <Video size={18} className="text-primary" />
                <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                  Media
                </h2>
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-primary/10 text-primary border border-primary/20">
                  {mediaItems.length}
                </span>
              </div>

              {/* Filter Pills */}
              <div className="flex flex-wrap items-center gap-1.5">
                {['all', ...SERVICE_REQUEST_MEDIA_FILE_TYPES].map((typeKey) => {
                  const count = getMediaCountByType(typeKey);
                  const isSelected = mediaTypeFilter === typeKey;
                  const label = typeKey === 'all' ? 'All' : SERVICE_REQUEST_MEDIA_FILE_TYPE_LABELS[typeKey] || typeKey;

                  return (
                    <button
                      key={typeKey}
                      type="button"
                      onClick={() => setMediaTypeFilter(typeKey)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all ${isSelected
                        ? 'bg-primary text-primary-content shadow-sm'
                        : 'bg-base-200/60 hover:bg-base-200 text-base-content/60 border border-base-content/5'
                        }`}
                    >
                      <span>{label}</span>
                      <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${isSelected ? 'bg-primary-content/20 text-primary-content' : 'bg-base-content/10 text-base-content/60'}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Media Grid or Empty State */}
            {mediaLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3.5">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="aspect-[16/10] bg-base-200/60 rounded-xl animate-pulse"></div>
                ))}
              </div>
            ) : filteredMediaItems.length === 0 ? (
              <div className="py-12 flex flex-col items-center justify-center text-center space-y-2">
                <div className="w-12 h-12 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30">
                  <Inbox size={24} />
                </div>
                <p className="text-sm font-bold text-base-content/60">
                  {mediaTypeFilter === 'all' ? 'No media uploaded' : `No ${SERVICE_REQUEST_MEDIA_FILE_TYPE_LABELS[mediaTypeFilter] || mediaTypeFilter} items`}
                </p>
                <p className="text-xs text-base-content/40 max-w-xs">
                  Captured media files and attachments for this {SERVICE_REQUEST_LABEL.singularLower} will appear here.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5">
                {filteredMediaItems.map((item) => (
                  <MediaCard
                    key={item.uid}
                    item={item}
                    canRemove={can.service_requests.deleteMedia()}
                    onRemove={setRemoveMediaTarget}
                    onOpen={setViewerItem}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Notes & Activity Timeline */}
        <div className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-4 space-y-6">
          <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
            {/* Notes / Activity Timeline Tab Headers */}
            <div role="tablist" className="flex items-end gap-5 mb-5 border-b border-base-content/10">
              {[
                { key: 'notes', label: 'Notes', icon: StickyNote, count: notes.length },
                { key: 'timeline', label: 'Activity Timeline', icon: Activity, count: events.length },
              ].map(({ key, label, icon: TabIcon, count }) => {
                const isActive = activityTab === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActivityTab(key)}
                    className={`-mb-px inline-flex items-center gap-2 pb-3 border-b-2 transition-colors ${isActive
                      ? 'border-primary text-base-content'
                      : 'border-transparent text-base-content/40 hover:text-base-content/70 hover:border-base-content/20'
                      }`}
                  >
                    <TabIcon size={18} className={isActive ? 'text-primary' : ''} />
                    <span className="text-sm font-black uppercase tracking-wider whitespace-nowrap">
                      {label}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${isActive
                      ? 'bg-primary/10 text-primary border-primary/20'
                      : 'bg-base-200 text-base-content/50 border-base-content/5'
                      }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="max-h-[70vh] overflow-y-auto pr-1">
              {activityTab === 'notes' ? (
                <>
                  <form onSubmit={handleAddNote} className="mb-5">
                    <textarea
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      maxLength={SERVICE_REQUEST_NOTE_MAX_LENGTH}
                      rows={3}
                      placeholder={`Add a note to this ${SERVICE_REQUEST_LABEL.singularLower}...`}
                      className="textarea textarea-bordered w-full rounded-xl text-sm leading-relaxed bg-base-200/40 focus:outline-none focus:border-primary"
                      disabled={addingNote}
                    />
                    <div className="flex items-center justify-between gap-3 mt-2">
                      <span className="text-[11px] text-base-content/40 font-medium">
                        {noteDraft.length}/{SERVICE_REQUEST_NOTE_MAX_LENGTH}
                      </span>
                      <button
                        type="submit"
                        className="btn btn-sm btn-primary rounded-xl gap-2 font-bold"
                        disabled={addingNote || !noteDraft.trim()}
                      >
                        {addingNote ? <span className="loading loading-spinner loading-xs"></span> : <StickyNote size={14} />}
                        Add Note
                      </button>
                    </div>
                  </form>

                  {notes.length === 0 ? (
                    <div className="py-8 flex flex-col items-center justify-center text-center space-y-2">
                      <div className="w-12 h-12 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30">
                        <StickyNote size={24} />
                      </div>
                      <p className="text-sm font-bold text-base-content/60">No notes yet</p>
                      <p className="text-xs text-base-content/40 max-w-xs">
                        Notes added to this {SERVICE_REQUEST_LABEL.singularLower} will appear here.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {notes.map((note) => {
                        const authorName = note.createdByName || 'Unknown user';
                        const initials = authorName
                          .split(' ')
                          .map((n) => n[0])
                          .join('')
                          .toUpperCase()
                          .slice(0, 2) || 'U';

                        return (
                          <div key={note.uid} className="bg-base-200/40 hover:bg-base-200/70 p-3 rounded-xl border border-base-content/5 transition-colors">
                            <p className="text-sm text-base-content leading-relaxed whitespace-pre-wrap break-words">
                              {note.note}
                            </p>
                            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-base-content/5 text-[11px] text-base-content/50 font-medium flex-wrap">
                              <span className="inline-flex items-center gap-1 font-bold text-base-content/70">
                                <span className="w-4 h-4 rounded-full bg-base-content/10 text-[9px] flex items-center justify-center font-black">
                                  {initials}
                                </span>
                                {authorName}
                              </span>
                              <span>·</span>
                              <span title={formatDateTime(note.createdAt)}>
                                {formatRelativeTime(note.createdAt)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {timelineLoading ? (
                    <div className="space-y-4 py-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex gap-3 items-start animate-pulse">
                          <div className="w-8 h-8 rounded-full bg-base-300 flex-shrink-0"></div>
                          <div className="flex-1 space-y-2">
                            <div className="h-4 bg-base-300 rounded w-3/4"></div>
                            <div className="h-3 bg-base-300 rounded w-1/2"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : events.length === 0 ? (
                    <div className="py-12 flex flex-col items-center justify-center text-center space-y-2">
                      <div className="w-12 h-12 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30">
                        <Activity size={24} />
                      </div>
                      <p className="text-sm font-bold text-base-content/60">No activity recorded</p>
                      <p className="text-xs text-base-content/40">Events and status updates will be logged here.</p>
                    </div>
                  ) : (
                    <div className="relative pl-1">
                      {/* Timeline connector track */}
                      <div className="space-y-4">
                        {events.map((event, idx) => {
                          const iconConfig = EVENT_ICON_CONFIG[event.eventType] || {
                            icon: Activity,
                            color: 'text-primary bg-primary/10 border-primary/20',
                          };
                          const EventIcon = iconConfig.icon;
                          const performerName = event.performedByName || 'System';
                          const initials = performerName
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .toUpperCase()
                            .slice(0, 2) || 'SY';

                          return (
                            <div key={event.uid} className="relative flex gap-3 items-start group">
                              {/* Timeline Node & Line */}
                              <div className="flex flex-col items-center flex-shrink-0">
                                <div
                                  className={`w-7 h-7 rounded-xl border flex items-center justify-center shadow-xs transition-transform group-hover:scale-110 ${iconConfig.color}`}
                                  title={SERVICE_REQUEST_EVENT_LABELS[event.eventType] || event.eventType}
                                >
                                  <EventIcon size={13} />
                                </div>
                                {idx < events.length - 1 && (
                                  <div className="w-0.5 flex-1 min-h-[28px] bg-base-content/10 my-1"></div>
                                )}
                              </div>

                              {/* Content */}
                              <div className="flex-1 pb-3 min-w-0">
                                <div className="bg-base-200/40 hover:bg-base-200/70 p-3 rounded-xl border border-base-content/5 transition-colors">
                                  <div>{renderEventDescription(event, types)}</div>

                                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-base-content/5 text-[11px] text-base-content/50 font-medium flex-wrap">
                                    <span className="inline-flex items-center gap-1 font-bold text-base-content/70">
                                      <span className="w-4 h-4 rounded-full bg-base-content/10 text-[9px] flex items-center justify-center font-black">
                                        {initials}
                                      </span>
                                      {performerName}
                                    </span>
                                    <span>·</span>
                                    <span title={formatDateTime(event.createdAt)}>
                                      {formatRelativeTime(event.createdAt)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} title={`Delete ${SERVICE_REQUEST_LABEL.singular}`}>
        <div className="mb-6 space-y-3">
          <p className="text-base-content/70 text-sm leading-relaxed">
            Are you sure you want to delete {SERVICE_REQUEST_LABEL.singularLower}{' '}
            <strong className="text-base-content font-mono px-1.5 py-0.5 rounded bg-base-200">
              {serviceRequest.requestNumber}
            </strong>?
          </p>
          <div className="p-3 rounded-xl bg-error/10 border border-error/20 text-error text-xs font-semibold">
            This action cannot be undone. All associated data will be removed.
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setDeleteConfirmOpen(false)}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
          >
            {deleting ? 'Deleting...' : `Delete ${SERVICE_REQUEST_LABEL.singular}`}
          </button>
        </div>
      </Modal>

      {/* Remove Media Confirmation Modal */}
      <Modal isOpen={!!removeMediaTarget} onClose={() => setRemoveMediaTarget(null)} title="Remove Media Item">
        <div className="mb-6 space-y-2">
          <p className="text-base-content/70 text-sm leading-relaxed">
            Are you sure you want to remove{' '}
            <strong className="text-base-content font-bold">{removeMediaTarget?.fileName}</strong>?
          </p>
          <p className="text-xs text-base-content/50">
            This media file will be detached from this {SERVICE_REQUEST_LABEL.singularLower}.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setRemoveMediaTarget(null)}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={removingMedia}
          >
            Cancel
          </button>
          <button
            onClick={handleRemoveMedia}
            disabled={removingMedia}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
          >
            {removingMedia ? 'Removing...' : 'Remove'}
          </button>
        </div>
      </Modal>

      {/* Public Link Modal */}
      <Modal
        isOpen={showLinkModal}
        onClose={() => {
          setShowLinkModal(false);
          setShowExpiryEditor(false);
        }}
        title="Public Link"
      >
        <div className="space-y-4">
          {publicLinkLoading ? (
            <div className="h-24 bg-base-200/60 rounded-xl animate-pulse" />
          ) : isLinkActive ? (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={publicLink.status} />
                <span className="text-xs text-base-content/60 font-medium" title={formatDateTime(publicLink.expiresAt)}>
                  Expires {formatFutureExpiry(publicLink.expiresAt)} ({formatDateTime(publicLink.expiresAt)})
                </span>
              </div>
              <div className="flex items-center gap-2 bg-base-200/60 border border-base-content/10 rounded-xl px-3 py-2.5">
                <p className="flex-1 text-sm font-mono text-base-content truncate">{publicLinkUrl}</p>
                <CopyButton text={publicLinkUrl} label="Public Link" />
              </div>
              <div className="flex justify-center">
                <QRCodeDisplay value={publicLinkUrl} size={96} />
              </div>
              <p className="text-xs text-base-content/50 leading-relaxed">
                Anyone with this link can view this {SERVICE_REQUEST_LABEL.singularLower}'s details and media, without logging in, until it expires or is revoked.
              </p>
              {can.service_requests.create() && (!showExpiryEditor ? (
                <div className="flex gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => setRevokeConfirmOpen(true)}
                    disabled={linkActionLoading}
                    className="flex-1 px-4 py-2.5 border border-error/30 text-error rounded-xl font-bold text-sm hover:bg-error/10 transition disabled:opacity-50"
                  >
                    Revoke
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowExpiryEditor(true)}
                    disabled={linkActionLoading}
                    className="flex-1 btn-premium justify-center"
                  >
                    Change Expiry
                  </button>
                </div>
              ) : (
                <div className="pt-3 border-t border-base-content/10 space-y-3">
                  <label className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                    New Expiry
                  </label>
                  <PublicLinkExpirySelector
                    key={publicLink ? `regen-${publicLink.code}` : 'regen-empty'}
                    onChange={(iso, valid) => {
                      setLinkExpiryIso(iso);
                      setIsExpiryValid(valid);
                    }}
                    disabled={linkActionLoading}
                  />
                  <div className="flex gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => setShowExpiryEditor(false)}
                      disabled={linkActionLoading}
                      className="flex-1 px-4 py-2.5 border border-base-content/15 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        const success = await handleGenerateOrRegenerateLink();
                        if (success) setShowExpiryEditor(false);
                      }}
                      disabled={linkActionLoading || !isExpiryValid || !linkExpiryIso}
                      className="flex-1 btn-premium justify-center"
                    >
                      {linkActionLoading ? 'Updating...' : 'Save Expiry'}
                    </button>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              {publicLink && (publicLink.status === 'expired' || publicLink.status === 'revoked') && (
                <div className="flex items-center gap-2">
                  <StatusBadge status={publicLink.status} />
                  <span className="text-xs text-base-content/50 font-medium">This link is no longer usable.</span>
                </div>
              )}
              {can.service_requests.create() ? (
                <>
                  <PublicLinkExpirySelector
                    key={showLinkModal ? 'gen-active' : 'gen-inactive'}
                    onChange={(iso, valid) => {
                      setLinkExpiryIso(iso);
                      setIsExpiryValid(valid);
                    }}
                    disabled={linkActionLoading}
                  />
                  <button
                    type="button"
                    onClick={handleGenerateOrRegenerateLink}
                    disabled={linkActionLoading || !isExpiryValid || !linkExpiryIso}
                    className="w-full btn-premium justify-center"
                  >
                    {linkActionLoading ? 'Generating...' : 'Generate Link'}
                  </button>
                </>
              ) : (
                !publicLink && (
                  <p className="text-sm text-base-content/60 text-center py-6">No public link generated</p>
                )
              )}
            </>
          )}
        </div>
      </Modal>

      {/* Revoke Public Link Confirmation Modal */}
      <Modal isOpen={revokeConfirmOpen} onClose={() => setRevokeConfirmOpen(false)} title="Revoke Public Link">
        <div className="mb-6 space-y-2">
          <p className="text-base-content/70 text-sm leading-relaxed">
            Are you sure you want to revoke this public link? Anyone using it will immediately lose access.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setRevokeConfirmOpen(false)}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={linkActionLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleRevokeLink}
            disabled={linkActionLoading}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
          >
            {linkActionLoading ? 'Revoking...' : 'Revoke Link'}
          </button>
        </div>
      </Modal>

      {/* Media Viewer Modal */}
      <MediaViewerModal item={viewerItem} onClose={() => setViewerItem(null)} />
    </div>
  );
};

export default ServiceRequestDetail;

